import { parseNumberList } from "@/lib/parse-number";
import type { DayNumber } from "@/lib/tool-kit/study/dates";

/**
 * The spaced repetition scheduler: review dates on expanding intervals, fitted in
 * before an exam.
 *
 * Intervals are counted from the day you first study a topic — the default 1, 3, 7, 14
 * and 30 means reviews one, three, seven, fourteen and thirty days later, so the gaps
 * between reviews grow (1, 2, 4, 7, 16 days). When the exam comes before the last
 * review would, every interval is scaled by the same factor so the last review lands
 * the day before the exam, each review stays at least a day after the one before, and
 * the plan never runs past the exam.
 */

export const DEFAULT_INTERVALS: readonly number[] = [1, 3, 7, 14, 30];
const MAX_INTERVAL = 365;

/** "1, 3, 7, 14, 30" → whole days from 1 to 365, unique, ascending. */
export function parseIntervals(raw: string): number[] {
  const values = parseNumberList(raw).filter(
    (value) => Number.isInteger(value) && value >= 1 && value <= MAX_INTERVAL
  );
  return [...new Set(values)].sort((a, b) => a - b);
}

/**
 * Fit ascending review offsets into `window` days (the last usable day is `window`).
 *
 * - Everything fits: unchanged.
 * - Fewer days than reviews: one review a day, every day.
 * - Otherwise each offset is scaled by `window ÷ last offset` and rounded, then held
 *   between "a day after the previous review" and "late enough to leave a day for each
 *   review still to come" — which pins the last one to `window`.
 */
export function fitOffsets(
  offsets: readonly number[],
  window: number
): number[] {
  const last = offsets.at(-1);
  if (window < 1 || last === undefined) {
    return [];
  }
  if (last <= window) {
    return [...offsets];
  }
  const count = offsets.length;
  if (window <= count) {
    return Array.from({ length: window }, (_, index) => index + 1);
  }
  const fitted: number[] = [];
  let previous = 0;
  for (const [index, offset] of offsets.entries()) {
    const scaled = Math.round((offset * window) / last);
    const latest = window - (count - 1 - index);
    const value = Math.min(Math.max(scaled, previous + 1), latest);
    fitted.push(value);
    previous = value;
  }
  return fitted;
}

export interface TopicReviews {
  /** Review dates, ascending. */
  days: DayNumber[];
  /** True when the intervals had to shrink to fit before the exam. */
  compressed: boolean;
  /** True when a last review was added the day before the exam. */
  finalAdded: boolean;
}

/**
 * Review dates for one topic.
 *
 * Reviews run from the day after first study to the day before the exam. With
 * `finalReview`, a schedule that ends well before the exam (two or more days of nothing)
 * gets one more review the day before it — a 30-day interval is no use to an exam in 60.
 */
export function topicReviews(
  firstStudy: DayNumber,
  exam: DayNumber,
  offsets: readonly number[],
  finalReview: boolean
): TopicReviews {
  const window = exam - firstStudy - 1;
  if (window < 1) {
    return { days: [], compressed: false, finalAdded: false };
  }
  const last = offsets.at(-1) ?? 0;
  const fitted = fitOffsets(offsets, window);
  const compressed = last > window;
  // With no intervals at all, the final review is the only one; otherwise it is added
  // only when it would not sit on the day after the last scheduled review.
  const lastFitted = fitted.at(-1);
  const gapBeforeExam = lastFitted === undefined ? 2 : window - lastFitted;
  const finalAdded = finalReview && gapBeforeExam >= 2;
  if (finalAdded) {
    fitted.push(window);
  }
  return {
    days: fitted.map((offset) => firstStudy + offset),
    compressed,
    finalAdded,
  };
}

export interface CalendarReview {
  /** Index into the topic list. */
  topic: number;
  /** 1-based review number for that topic. */
  number: number;
  /** How many reviews that topic has in total. */
  of: number;
}

export interface ReviewDay {
  day: DayNumber;
  reviews: CalendarReview[];
}

/** Every topic's reviews merged into one date-ordered list. */
export function mergeReviewDays(plans: readonly TopicReviews[]): ReviewDay[] {
  const byDay = new Map<DayNumber, CalendarReview[]>();
  for (const [topic, plan] of plans.entries()) {
    for (const [index, day] of plan.days.entries()) {
      const list = byDay.get(day) ?? [];
      list.push({ topic, number: index + 1, of: plan.days.length });
      byDay.set(day, list);
    }
  }
  return [...byDay.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([day, reviews]) => ({ day, reviews }));
}
